import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { t } from 'i18next'
import api from '@/core/services/api'
import { getBaseCurrency } from '@/constant/currency'
import { setUserLogin } from '@/core/store/user/actions'
import { usePopup } from '@/core/context/useWindow'
import Login from '@/components/popup/Login'
import GoldTermsModal from './GoldTermsModal'
import GoldSelect from './GoldSelect'
import GoldDatePicker from './GoldDatePicker'
import { getPromoCode } from '@/core/promoLink'
import registerBg from '@/assets/image/gold/register_bg.png'
import iconsSprite from '@/assets/icons.svg'
import { G } from './theme'

/**
 * 회원가입 창 — gold8.bet PC .registerDialog 실측: 폭 750 = 왼쪽 그림 340 + 오른쪽 폼 410(높이 640, 안에서 스크롤),
 *   바탕 #1a242d, 화면 위 10%. 항목 순서·안내 문구는 gold 와 같다(★ = 필수).
 *   보이는 항목·필수 여부는 사이트 가입 항목 설정(/auth/sign-up/fields = user_signup_field):
 *     - 아이디·비밀번호·비밀번호 확인·출금 비밀번호는 늘 필수.
 *     - 추천인(join_code)은 설정 행이 없으면 필수(기존 가입 규칙), 비워 두면 서버가 사이트 기본 매장으로 가입시킨다.
 *     - 닉네임은 nickname use_yn=N 이면 묻지 않는다(서버가 아이디를 닉네임으로 쓴다).
 *     - 나머지는 use_yn=Y 인 것만 보이고 required_yn=Y 면 ★.
 *   은행 목록은 사이트 은행 관리(/auth/bank = bank_mst). 출금 계좌 예금주는 서버가 실명으로 저장한다.
 *   매장 홍보 링크(?a=가입코드)로 들어왔으면 기억해 둔 코드를 추천인 칸에 미리 채운다(고칠 수 있음, 사용자 지시 2026-10-19).
 *   선택 상자·생년월일은 브라우저 기본 목록·달력 대신 직접 그린 GoldSelect·GoldDatePicker(사용자 지시 2026-10-19).
 *   가입 결과: 바로 승인되는 매장이면 로그인된 채로 새로고침, 승인 대기 매장이면 대기 안내.
 */

// [라벨 키(goldRegister.*), 보낼 값 이름, 입력 종류]
const FIELDS = [
  ['parent', 'join_code', 'text'],
  ['account', 'id', 'text'],
  ['nickname', 'nickname', 'text'],
  ['password', 'password', 'password'],
  ['checkPassword', 'checkPassword', 'password'],
  ['moneyPassword', 'account_out_password', 'password'],
  ['name', 'real_name', 'text'],
  ['mobile', 'phone_number', 'mobile'],
  ['email', 'email', 'text'],
  ['sex', 'gender', 'sex'],
  ['birthday', 'birth_date', 'date'],
  ['wechat', 'wechat', 'text'],
  ['qq', 'q_number', 'text'],
  ['bank', 'account_bank', 'bank'],
  ['prefecture', 'prefecture', 'text'],
  ['city', 'city', 'text'],
  ['idNumber', 'id_number', 'text'],
  // 지점 번호(branch_no) + 계좌번호(account_no) 한 묶음
  ['branchAccount', 'branch_no', 'branch'],
]
/** 설정과 상관없이 늘 받는 필수 항목 */
const ALWAYS_REQUIRED = new Set(['id', 'password', 'checkPassword', 'account_out_password'])
/** 입력 길이 — 서버 검증·저장 칸 길이(xxx-user-back services/signupFieldService.js)와 같다 */
const MAX_LEN = {
  join_code: 20, id: 20, nickname: 50, password: 30, checkPassword: 30, account_out_password: 30,
  real_name: 50, phone_number: 20, email: 254, wechat: 100, q_number: 50,
  prefecture: 100, city: 100, id_number: 50, branch_no: 20, account_no: 50,
}
/** 휴대폰 국가번호 — gold 가입 창과 같은 목록 */
const PHONE_COUNTRY_CODES = ['+81']
const ID_RE = /^[a-zA-Z0-9._-]+$/
const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[\W_]).{8,30}$/
/** 서버 오류 path → 이 창의 값 이름 */
const ERROR_PATH = { code: 'join_code', contry_no: 'phone_number' }
/** 값 이름 → 라벨 키 */
const LABEL_KEY = { ...Object.fromEntries(FIELDS.map(([key, name]) => [name, key])), account_no: 'branchAccount' }

/** 생년월일 허용 범위 — 만 14~120세(xxx-user-back signupFieldService 와 같은 계산). 달력 min/max 로도 쓴다 */
const birthRange = () => {
  const now = new Date()
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return {
    min: ymd(new Date(now.getFullYear() - 120, now.getMonth(), now.getDate())),
    max: ymd(new Date(now.getFullYear() - 14, now.getMonth(), now.getDate())),
  }
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  overflow-y: auto;
`

const Dialog = styled.div`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  width: 750px;
  max-width: calc(100% - 20px);
  background: ${G.panel};
`

const Close = styled.button`
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 3;
  padding: 0;
  border: 0;
  background: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
`

const Left = styled.div`
  position: relative;
  flex: 0 0 340px;
  background: url(${registerBg}) center / cover no-repeat;
  @media (max-width: 760px) { display: none; }
`

const LeftText = styled.span`
  position: absolute;
  left: 35px;
  bottom: 100px;
  width: 250px;
  font-size: 24px;
  font-weight: 700;
  color: #000;
`

const Right = styled.div`
  flex: 1;
  height: 640px;
  padding: 20px 10px;
  overflow: auto;
  &::-webkit-scrollbar { width: 7px; }
  &::-webkit-scrollbar-thumb { border-radius: 10px; background: #41586d; }
  &::-webkit-scrollbar-track { background: #0c151d; }
`

const Title = styled.div`
  margin-bottom: 18px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
`

const Group = styled.div`
  position: relative;
  margin-bottom: 22px;
`

const Label = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`

const Star = styled.span`
  color: #f5c518;
  font-size: 14px;
`

const inputCss = `
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: ${G.bg};
  font-size: 14px;
  color: ${G.sand};
  outline: none;
`

const ERROR_RED = '#ff4d5e'

const Input = styled.input`
  && {
    ${inputCss}
    box-shadow: ${({ $error }) => ($error ? `inset 0 0 0 1px ${ERROR_RED}` : 'none')};
  }
`

const Row = styled.div`
  display: flex;
  gap: 8px;
`

const PwWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const Eye = styled.button`
  position: absolute;
  right: 12px;
  display: flex;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  cursor: pointer;
  svg { width: 100%; height: 100%; fill: currentColor; color: inherit; }
`

const Help = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${G.sand};
`

const ErrorMsg = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${ERROR_RED};
`

const Radio = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 18px;
  font-size: 14px;
  color: #fff;
`

const Agree = styled.label`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  margin: 40px 0 16px;
  text-align: center;
  font-size: 16px;
  color: #fff;
  a { color: ${G.gold}; cursor: pointer; }
`

const Submit = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 5px;
  background: linear-gradient(to right, #ffef77, #fff0a7);
  font-size: 16px;
  font-weight: 700;
  color: ${G.bg};
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`

const LinkLine = styled.div`
  margin-top: 20px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  button {
    margin-left: 10px;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;
    font-weight: 700;
    color: ${G.gold};
    cursor: pointer;
  }
`

/** 오류 코드 문구(error2.*) — {{param}} 은 이 창의 항목 이름, {{valueN}} 은 서버가 준 값 */
const errorText = (code, name, values = []) => {
  let msg = t(`error2.${code}`).replace(/\{\{param\}\}/g, t(`goldRegister.${LABEL_KEY[name] || name}`))
  values.forEach((v, i) => { msg = msg.replace(new RegExp(`\\{\\{value${i}\\}\\}`, 'g'), v) })
  return msg
}

export default function GoldRegister() {
  const dispatch = useDispatch()
  const { openPopup, closePopup } = usePopup()
  const [show, setShow] = useState({})
  const [meta, setMeta] = useState(null) // Map(field_code → 설정 행), null = 불러오는 중
  const [banks, setBanks] = useState([])
  // 홍보 링크 가입코드(쿠키 agentId) — 있으면 추천인 칸에 미리 채운다
  const [promoCode] = useState(getPromoCode)
  const [form, setForm] = useState(() => (promoCode ? { join_code: promoCode } : {}))
  const [countryCode, setCountryCode] = useState(PHONE_COUNTRY_CODES[0])
  const [errors, setErrors] = useState({})
  const [generalError, setGeneralError] = useState('')
  const [agree, setAgree] = useState(true)
  const [showTerms, setShowTerms] = useState(false) // 「ユーザー登録契約」 약관 창(가입 창 위에 겹쳐 띄워 입력값 유지)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    api.get('/auth/sign-up/fields')
      .then((res) => setMeta(new Map((res.data?.content || []).map((r) => [r.field_code, r]))))
      .catch(() => setMeta(new Map()))
    api.get('/auth/bank')
      .then((res) => setBanks(Array.isArray(res.data?.content) ? res.data.content : []))
      .catch(() => setBanks([]))
  }, [])

  const rowUsed = (code) => meta?.get(code)?.use_yn === 'Y'
  const rowRequired = (code) => rowUsed(code) && meta.get(code).required_yn === 'Y'

  /** 항목을 보일지·필수인지 */
  const fieldState = (name) => {
    if (ALWAYS_REQUIRED.has(name)) return { show: true, required: true }
    const row = meta?.get(name)
    if (name === 'join_code') {
      if (!row) return { show: true, required: true }
      return { show: rowUsed(name), required: rowRequired(name) }
    }
    if (name === 'nickname') {
      const asked = !row || row.use_yn === 'Y'
      return { show: asked, required: asked }
    }
    if (name === 'branch_no') {
      return {
        show: rowUsed('branch_no') || rowUsed('account_no'),
        required: rowRequired('branch_no') || rowRequired('account_no'),
      }
    }
    return { show: rowUsed(name), required: rowRequired(name) }
  }

  const setValue = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev))
    setGeneralError('')
  }

  const valueOf = (name) => (name.toLowerCase().includes('password') ? (form[name] ?? '') : String(form[name] ?? '').trim())

  /** 보이는 항목 이름 목록(지점·계좌 묶음은 각각) */
  const visibleNames = () => {
    const names = []
    for (const [, name] of FIELDS) {
      if (!fieldState(name).show) continue
      if (name === 'branch_no') {
        if (rowUsed('branch_no')) names.push('branch_no')
        if (rowUsed('account_no')) names.push('account_no')
      } else {
        names.push(name)
      }
    }
    return names
  }

  /** 화면에서 먼저 거르는 검사(필수·아이디·비밀번호 규칙·비밀번호 확인). 형식 검사는 서버가 한다 */
  const validate = (names) => {
    const errs = {}
    for (const name of names) {
      const required = name === 'account_no' || name === 'branch_no' ? rowRequired(name) : fieldState(name).required
      if (required && valueOf(name) === '') errs[name] = errorText('M001', name)
    }
    const id = valueOf('id')
    if (!errs.id && (id.length < 6 || id.length > 20)) errs.id = errorText('M203', 'id', ['6', '20'])
    else if (!errs.id && !ID_RE.test(id)) errs.id = errorText('M062', 'id')
    if (!errs.password && !PASSWORD_RE.test(valueOf('password'))) errs.password = errorText('M063', 'password')
    if (!errs.checkPassword && valueOf('checkPassword') !== valueOf('password')) errs.checkPassword = errorText('M003', 'password')
    const birth = valueOf('birth_date')
    if (!errs.birth_date && birth !== '' && names.includes('birth_date')) {
      const { min, max } = birthRange()
      if (birth < min || birth > max) errs.birth_date = errorText('M066', 'birth_date', [min, max])
    }
    if (!errs.nickname && names.includes('nickname')) {
      const nick = valueOf('nickname')
      if (nick.length < 4 || nick.length > 50) errs.nickname = errorText('M203', 'nickname', ['4', '50'])
    }
    return errs
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (submitting || !meta || !agree) return

    const names = visibleNames()
    const errs = validate(names)
    setErrors(errs)
    setGeneralError('')
    if (Object.keys(errs).length > 0) return

    // 보이는 항목 중 입력한 값만 보낸다(국가번호는 휴대폰 번호가 있을 때만)
    const body = { currency_type: getBaseCurrency() }
    for (const name of names) {
      if (name === 'checkPassword') continue
      const v = valueOf(name)
      if (v !== '') body[name] = v
    }
    if (body.phone_number) body.contry_no = countryCode
    // 사이트 설정으로 추천인 칸을 안 보이는 경우에도 홍보 링크 코드는 보낸다
    if (!body.join_code && promoCode && !fieldState('join_code').show) body.join_code = promoCode

    setSubmitting(true)
    try {
      const res = await api.post('/auth/sign-up', body)
      // 바로 승인되는 매장: 서버가 로그인까지 끝냈다
      dispatch(setUserLogin(res.data.content))
      window.location.reload()
    } catch (err) {
      const data = err?.response?.data
      if (err?.response?.status === 400 && data?.errors === 'signup_wait_limit') {
        // 가입은 끝났고 매장 승인 대기
        openPopup({ title: t('goldRegister.title'), content: `${t('error.signup_wait_limit')} ${t('error.signup_wait_limit2')}` })
        return
      }
      const list = Array.isArray(data?.errors) ? data.errors : []
      const next = {}
      let general = ''
      for (const it of list) {
        const name = ERROR_PATH[it?.path] || it?.path
        const code = it?.msg?.code
        // 유저 서버는 생년월일 범위 밖도 M054 로 준다 — 날짜는 달력 값이라 형식 오류일 수 없으니 범위 안내로 보인다
        if (name === 'birth_date' && code === 'M054' && !next[name]) {
          const { min, max } = birthRange()
          next[name] = errorText('M066', name, [min, max])
        } else if (code && LABEL_KEY[name] && !next[name]) next[name] = errorText(code, name, it.msg.values || [])
        else if (!general) general = t('goldRegister.failed')
      }
      if (!list.length) general = t('goldRegister.failed')
      setErrors(next)
      setGeneralError(general)
    } finally {
      setSubmitting(false)
    }
  }

  const openLogin = () => openPopup({ grandLogin: true, title: t('modalTitle.login'), content: <Login /> })

  // 입력칸 오류가 하나라도 남아 있으면 가입 버튼 아래에 「가입정보 확인」 안내(칸을 고치면 그 칸 오류가 지워진다)
  const hasFieldError = Object.values(errors).some(Boolean)

  const inputProps = (name) => ({
    value: form[name] ?? '',
    maxLength: MAX_LEN[name],
    onChange: (e) => setValue(name, e.target.value),
    $error: !!errors[name],
  })

  const renderControl = (key, name, type) => {
    if (type === 'password') {
      return (
        <PwWrap>
          <Input
            {...inputProps(name)}
            type={show[key] ? 'text' : 'password'}
            autoComplete="new-password"
            style={{ paddingRight: 40 }}
          />
          <Eye
            type="button"
            aria-label={show[key] ? 'hide password' : 'show password'}
            onClick={() => setShow(s => ({ ...s, [key]: !s[key] }))}
          >
            <svg viewBox={show[key] ? '0 0 488.85 488.85' : '0 0 24 24'} aria-hidden="true">
              <use href={`${iconsSprite}#${show[key] ? 'ico-eyeon' : 'ico-eyeoff'}`} />
            </svg>
          </Eye>
        </PwWrap>
      )
    }
    if (type === 'mobile') {
      return (
        <Row>
          <GoldSelect
            style={{ width: 80, flex: '0 0 80px' }}
            value={countryCode}
            ariaLabel="country code"
            options={PHONE_COUNTRY_CODES.map((c) => ({ value: c, label: c }))}
            onChange={setCountryCode}
          />
          <Input {...inputProps(name)} type="tel" autoComplete="tel-national" />
        </Row>
      )
    }
    if (type === 'sex') {
      return (
        <div>
          <Radio>
            <input type="radio" name="gold-sex" checked={form.gender === 'M'} onChange={() => setValue('gender', 'M')} />
            {t('goldRegister.male')}
          </Radio>
          <Radio>
            <input type="radio" name="gold-sex" checked={form.gender === 'F'} onChange={() => setValue('gender', 'F')} />
            {t('goldRegister.female')}
          </Radio>
        </div>
      )
    }
    if (type === 'date') {
      const { min, max } = birthRange()
      return <GoldDatePicker value={form[name] ?? ''} min={min} max={max} error={!!errors[name]} onChange={(v) => setValue(name, v)} />
    }
    if (type === 'bank') {
      return (
        <GoldSelect
          value={form.account_bank ?? ''}
          placeholder={t('goldRegister.bankPlaceholder')}
          error={!!errors.account_bank}
          options={[{ value: '', label: t('goldRegister.bankPlaceholder') }, ...banks.map((b) => ({ value: b.value, label: b.label }))]}
          onChange={(v) => setValue('account_bank', v)}
        />
      )
    }
    if (type === 'branch') {
      const branch = rowUsed('branch_no')
      const account = rowUsed('account_no')
      return (
        <>
          {branch && (
            <>
              <Input {...inputProps('branch_no')} type="text" inputMode="numeric" />
              <Help>{t('goldRegister.branchHelp')}</Help>
            </>
          )}
          {branch && account && <Help style={{ margin: '0 0 0' }}>-</Help>}
          {account && (
            <>
              <Input {...inputProps('account_no')} type="text" inputMode="numeric" />
              <Help>{t('goldRegister.accountNoHelp')}</Help>
            </>
          )}
        </>
      )
    }
    return (
      <Input
        {...inputProps(name)}
        type="text"
        inputMode={name === 'q_number' ? 'numeric' : undefined}
        autoComplete={name === 'id' ? 'username' : 'off'}
      />
    )
  }

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) closePopup() }}>
      <Dialog role="dialog" aria-modal="true">
        <Close type="button" aria-label="close" onClick={closePopup}>✕</Close>
        <Left><LeftText>{t('goldRegister.lead')}</LeftText></Left>
        <Right>
          <Title>{t('goldRegister.title')}</Title>
          {meta && (
            <form onSubmit={onSubmit} noValidate>
              {FIELDS.map(([key, name, type]) => {
                const state = fieldState(name)
                if (!state.show) return null
                const err = type === 'branch' ? (errors.branch_no || errors.account_no) : errors[name]
                return (
                  <Group key={key}>
                    <Label>{state.required && <Star>★</Star>}{t(`goldRegister.${key}`)}</Label>
                    {renderControl(key, name, type)}
                    {err
                      ? <ErrorMsg role="alert">{err}</ErrorMsg>
                      : (type !== 'sex' && type !== 'bank' && type !== 'branch' && <Help>{t(`goldRegister.${key}Help`)}</Help>)}
                  </Group>
                )
              })}
              <Agree>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>
                  {t('goldRegister.agreeBefore')}
                  <a href="#terms" onClick={(e) => { e.preventDefault(); setShowTerms(true) }}>{t('goldRegister.agreeLink')}</a>
                  {t('goldRegister.agreeAfter')}
                </span>
              </Agree>
              <Submit type="submit" disabled={!agree || submitting}>
                {t('goldRegister.submit')} <span aria-hidden="true">→</span>
              </Submit>
              {/* 가입 실패 안내 — 입력칸 오류가 남아 있으면 「가입정보 확인」, 아니면 서버 실패 문구 */}
              {(hasFieldError || generalError) && (
                <ErrorMsg role="alert" style={{ margin: '12px 0 0', textAlign: 'center' }}>
                  {hasFieldError ? t('goldRegister.checkInfo') : generalError}
                </ErrorMsg>
              )}
              <LinkLine>
                {t('goldRegister.haveAccount')}
                <button type="button" onClick={openLogin}>{t('goldRegister.signIn')}</button>
              </LinkLine>
            </form>
          )}
        </Right>
      </Dialog>
      {showTerms && <GoldTermsModal onClose={() => setShowTerms(false)} />}
    </Overlay>
  )
}
